import { AnyAction } from 'redux';
import {
  App,
  isMultistoreApp,
  StandaloneProdukt,
  Vorvertrag,
} from '@ps20-frontend/frontend-models';
import { GlobalActions } from '@ps20-frontend/shop-integration-redux';
import { Umstellungsstatus } from '@ps20/bff-models';
import { IAppState } from '../appState';
import { DashboardActions } from '../dashboard.actions';
import {
  isStandaloneVertrag,
  isVorvertrag,
  selectUmstellungsstatusForStandaloneProdukt,
  selectUmstellungsstatusForVorvertrag,
} from '../selectors/vorvertragsUebersicht.selector';

export interface VorvertragHinweis {
  standaloneProdukteVorhanden: boolean;
  umstellbareVorvertraegeVorhanden: boolean;
  mehrereVorvertraegeNichtUmstellbar: boolean;
  mehrereVorvertraegeUmstellbar: boolean;
  assekuranztarifChanged?: boolean;
  assekuranztarifMixed?: boolean;
  assekuranztarifVorbelegt?: boolean;
  maxAnzahlVorvertraegeErreicht?: boolean;
}

export const vorvertragHinweisReducer = function (
  sectionState: VorvertragHinweis = null,
  action: AnyAction,
  state: IAppState,
): VorvertragHinweis {
  switch (action.type) {
    case DashboardActions.UPDATE_FREMDVERTRAEGE_FINISHED:
    case DashboardActions.HANDLE_HINWEISE_VORVERTRAEGE: {
      const vorvertraege = getAlleVorvertraege(state);
      const standaloneProdukte = getAlleStandaloneProdukte(state);
      const vorvertraegeWithoutMPF = vorvertraege.filter(vertrag =>
        vertrag?.produktIds.some(id => !isMultistoreApp(id)),
      );

      return {
        ...sectionState,
        standaloneProdukteVorhanden:
          standaloneProdukte &&
          standaloneProdukte.some(
            produkt =>
              selectUmstellungsstatusForStandaloneProdukt(state.warenkorb, produkt) ===
              Umstellungsstatus.EINSCHLIESSBAR,
          ),
        umstellbareVorvertraegeVorhanden: state.vorvertraege.some(vorvertrag =>
          [Umstellungsstatus.UMSTELLBAR, Umstellungsstatus.OPTIONAL_UMSTELLBAR].includes(
            selectUmstellungsstatusForVorvertrag(vorvertrag, state),
          ),
        ),
        mehrereVorvertraegeNichtUmstellbar: mehrereVorvertraegeZuDemselbenProduktVorhanden(
          vorvertraege,
          [Umstellungsstatus.NICHT_UMSTELLBAR],
          state,
        ),
        mehrereVorvertraegeUmstellbar: mehrereVorvertraegeZuDemselbenProduktVorhanden(
          vorvertraegeWithoutMPF,
          [Umstellungsstatus.UMSTELLBAR, Umstellungsstatus.OPTIONAL_UMSTELLBAR],
          state,
        ),
      };
    }
    case GlobalActions.ASSEKURANZTARIF_CHANGED_VORVERTRAG: {
      return {
        ...sectionState,
        assekuranztarifChanged: action.payload,
      };
    }
    case GlobalActions.ASSEKURANZTARIF_MIXED: {
      return {
        ...sectionState,
        assekuranztarifMixed: action.payload,
      };
    }
    case GlobalActions.ASSEKURANZTARIF_VORBELEGT: {
      return {
        ...sectionState,
        assekuranztarifVorbelegt: true,
      };
    }
    case GlobalActions.UPDATE_VORVERTRAEGE: {
      return {
        ...sectionState,
        maxAnzahlVorvertraegeErreicht: action.payload.maxAnzahlVorvertraegeErreicht,
      };
    }
    default:
      return sectionState;
  }
};

function mehrereVorvertraegeZuDemselbenProduktVorhanden(
  vorvertraege: Vorvertrag[],
  vertragStatus: Umstellungsstatus[],
  state: IAppState,
): boolean {
  return (
    vorvertraege &&
    vorvertraege.some(vertrag =>
      vertrag.produktIds.some(
        produktId =>
          getVorvertraegeByAppIdAndUmstellungsStatus(vorvertraege, state, produktId, vertragStatus)
            .length > 1,
      ),
    )
  );
}

function getVorvertraegeByAppIdAndUmstellungsStatus(
  vorvertraege: Vorvertrag[],
  state: IAppState,
  appId: App,
  umstellungsstatus: Umstellungsstatus[],
): Vorvertrag[] {
  return vorvertraege
    .filter(vorvertrag => vorvertrag.produktIds.includes(appId))
    .filter(vorvertrag =>
      umstellungsstatus.includes(selectUmstellungsstatusForVorvertrag(vorvertrag, state)),
    );
}

function getAlleVorvertraege(state: IAppState): Vorvertrag[] {
  return (state.fremdVertraege || [])
    .concat(state.vorvertraege)
    .filter(fremdVertrag => isVorvertrag(fremdVertrag)) as Vorvertrag[];
}

function getAlleStandaloneProdukte(state: IAppState): StandaloneProdukt[] {
  return (state.fremdVertraege || [])
    .concat(state.standaloneProdukte)
    .filter(fremdVertrag => isStandaloneVertrag(fremdVertrag)) as StandaloneProdukt[];
}
